import { DEFAULT_MINOR_UNIT_EXPONENT, MINOR_UNIT_EXPONENTS } from './exponents.generated';
/**
 * How many minor-unit digits `currency` has, per ISO-4217. `2` for EUR/DZD/EGP, `3` for TND/KWD, `0` for
 * JPY/XOF/XAF.
 *
 * An unknown or empty code falls back to 2 rather than throwing: this sits on rendering paths, and an order
 * taken years ago in a since-withdrawn currency still has to appear on the seller's screen. Validation
 * belongs on the backend's write path.
 */
export function minorUnitExponent(currency) {
    if (!currency)
        return DEFAULT_MINOR_UNIT_EXPONENT;
    return MINOR_UNIT_EXPONENTS[currency.trim().toUpperCase()] ?? DEFAULT_MINOR_UNIT_EXPONENT;
}
/** The power of ten separating minor units from major ones: 100 for EUR, 1000 for KWD, 1 for JPY. */
export function minorUnitFactor(currency) {
    return 10 ** minorUnitExponent(currency);
}
/**
 * Integer minor units → major units as a number: `1234` KWD → `1.234`, `1000` JPY → `1000`.
 *
 * Use this only where a NUMBER is required — an analytics `value`, a chart datum. For anything a person
 * reads, use {@link formatMoney}, which also gets the digit count and the symbol right.
 */
export function toMajor(amountMinor, currency) {
    return amountMinor / minorUnitFactor(currency);
}
/**
 * A seller's typed major-unit input → integer minor units: `"1.234"` KWD → `1234`, `"1000"` JPY → `1000`.
 *
 * The direction the old code got wrong just as often as the other one: `Math.round(parseFloat(s) * 100)`
 * appears eleven times in the dashboard, so typing a Kuwaiti price of 1.234 stored 123 — a tenth of it.
 *
 * Returns `null` for anything that is not a finite number, so a caller can keep a field invalid rather than
 * silently storing `NaN`. Rounds half-up on the last digit the currency has: a seller typing three decimals
 * in EUR gets them rounded, not truncated.
 */
export function fromMajor(input, currency) {
    const exponent = minorUnitExponent(currency);
    // Shifting the decimal point on the DIGITS, never `value * 100`. A binary multiply cannot do this
    // correctly: 1.005 is stored as 1.00499999999999989, so both `Math.round(1.005 * 100)` and
    // `(1.005 * 100).toFixed(0)` yield 100 — a centime lost on a price the seller typed exactly. Even
    // `(1.005).toFixed(2)` returns "1.00" for the same reason. Working on the string is the only way to get
    // the answer a person expects, and it is what the backend does with BigDecimal.
    let text;
    if (typeof input === 'number') {
        if (!Number.isFinite(input))
            return null;
        // A number has already been through binary, so its exact decimal expansion is not what the seller
        // typed. Round it to a couple of digits past the currency's precision first — that recovers the
        // intended decimal ("1.00500") before the shift below reads it.
        text = input.toFixed(Math.min(20, exponent + 3));
    }
    else {
        text = String(input).replace(',', '.').trim();
    }
    const match = /^([+-]?)(\d*)(?:\.(\d*))?$/.exec(text);
    if (!match)
        return null;
    const [, sign, whole = '', decimals = ''] = match;
    if (whole === '' && decimals === '')
        return null;
    const kept = decimals.slice(0, exponent).padEnd(exponent, '0');
    const nextDigit = decimals.charAt(exponent);
    const digits = `${whole || '0'}${kept}`;
    let scaled = Number(digits);
    if (!Number.isFinite(scaled))
        return null;
    // Half-up on the first digit the currency cannot hold, matching the backend's RoundingMode.HALF_UP.
    if (nextDigit !== '' && Number(nextDigit) >= 5)
        scaled += 1;
    return sign === '-' ? -scaled : scaled;
}
/**
 * `{ amount, currency }` → a localised string: `"1 234,56 MAD"`, `"1,234 KWD"`, `"¥1,000"`.
 *
 * Divides by the ISO exponent (rule 1 above), then hands the number to `Intl`, which owns everything about
 * how it is written: grouping separator, symbol, symbol placement, RTL. `minimumFractionDigits` is pinned to
 * the ISO exponent so a three-decimal currency is not silently rendered with two — `Intl` alone would print
 * `1.23 KWD` for a 1.234 dinar amount, because CLDR thinks Kuwait writes two decimals.
 *
 * Falls back to `"<amount> <CODE>"` if `Intl` rejects the currency code (an old order in a code CLDR has
 * since dropped): a plain string the seller can still read, never a crash and never a blank cell.
 */
export function formatMoney(money, locale = 'fr', options = {}) {
    const exponent = minorUnitExponent(money.currency);
    const major = toMajor(money.amount, money.currency);
    try {
        // Compact notation must override BOTH digit bounds: leaving minimumFractionDigits at the currency's
        // exponent while capping the maximum at 1 makes min > max, which Intl rejects with a RangeError — and
        // the catch below would then print an unformatted string on every dashboard tile.
        // `whole` drops the minor units entirely — "58 $US", never "58,41 $US". It exists for labels painted
        // ON a chart, where the centimes are the characters that make two neighbouring labels overlap and
        // nobody reads them anyway. The exact amount always stays somewhere else on the same screen.
        const digits = options.compact
            ? { notation: 'compact', minimumFractionDigits: 0, maximumFractionDigits: 1 }
            : options.whole
                ? { minimumFractionDigits: 0, maximumFractionDigits: 0 }
                : { minimumFractionDigits: exponent, maximumFractionDigits: exponent };
        const formatter = new Intl.NumberFormat(locale, {
            style: 'currency',
            currency: money.currency,
            ...digits,
        });
        return isArabicLocale(locale)
            ? arabicLeftToRight(withFrenchNumbers(formatter, major, digits))
            : formatter.format(major);
    }
    catch {
        return `${major.toFixed(exponent)} ${money.currency}`;
    }
}
/**
 * Does this shop read Arabic? Sub-tags included — `ar`, `ar-MA`, `ar-EG` all count.
 *
 * Exported because the COD funnel asks the SAME question (`formatOfferMoney` in ui-react): an Arabic shop
 * must print `899,00 د.م.` in its order form exactly as it does under its product title, and two
 * definitions of "Arabic" would be two answers on the same page.
 */
export function isArabicLocale(locale) {
    return !!locale && locale.trim().toLowerCase().startsWith('ar');
}
/**
 * IN ARABIC, THE NUMBER IS WRITTEN THE FRENCH WAY — `1 500,50`. The currency's symbol, its placement and
 * the direction marks around it still come from the shop's locale.
 *
 * Founder decision, 18/08, and it settles a real defect: we pass the shop's LANGUAGE to `Intl`, and `ar`
 * with no country is the CLDR root rather than Morocco. So switching a Moroccan shop from French to Arabic
 * silently rewrote every price from `1 500,50` to `1,500.50` — a different convention, on the same shop,
 * for the same buyers, because of a setting about words. `ar-EG` would go further and change the DIGITS.
 *
 * Scoped to Arabic on purpose. An English shop writing `1,234.56` is not a defect, it is English, and
 * forcing French on it would be the same mistake in the other direction.
 *
 * It also closes half of a second inconsistency the lot 5 pass found: `formatOfferMoney` (the COD tiles and
 * totals) is deterministic and already writes `1 500,50`, so the two formatters printed different numbers
 * on the same product page. They now agree on the number; only the symbol still differs.
 *
 * Implemented over `formatToParts` rather than by formatting in `fr` outright, because the symbol is NOT
 * ours to change: an Arabic shop keeps `د.م.` and its right-to-left marks, and only the digits move.
 */
function withFrenchNumbers(formatter, major, digits) {
    const french = new Intl.NumberFormat('fr', digits).format(major);
    let written = false;
    return formatter
        .formatToParts(major)
        .map((part) => {
        if (!NUMERIC_PARTS.has(part.type))
            return part.value;
        // The number is emitted as several parts (integer, group, decimal, fraction, and for compact
        // notation a unit suffix). Replace the run ONCE and drop the rest, or "1 500,50" would be spliced
        // in at every one of them.
        if (written)
            return '';
        written = true;
        return french;
    })
        .join('');
}
/**
 * EN ARABE, LE MONTANT SE LIT DE GAUCHE À DROITE — « 899,00 د.م. », jamais « د.م. 899,00 ».
 *
 * Fondatrice, 23/10/2026 : « je veux que la devise s'écrive left to right comme le fr », puis, capture à
 * l'appui, « nrmlm ça doit etre comme ça 899,00 د.م. ». Donc : le SYMBOLE est celui de sa langue, l'ORDRE
 * est celui du français — les chiffres d'abord, le symbole après, à leur droite.
 *
 * Deux choses s'y opposaient, et il faut les deux :
 *  1. `Intl` entoure lui-même le montant arabe de deux marques invisibles (U+200F RIGHT-TO-LEFT MARK).
 *     Elles font de toute la chaîne un bloc de droite à gauche — y compris à l'intérieur d'un élément mis
 *     en `direction:ltr`, qui ne peut donc PAS les contredire. On les retire.
 *  2. Une fois nues, les chiffres et le symbole redeviennent deux blocs que le paragraphe arabe pose de
 *     droite à gauche. {@link ltrIsolate} les enferme dans un isolat de gauche à droite : deux points de
 *     code invisibles, de largeur nulle, qui ne changent ni la longueur lue à l'écran ni le sens des mots
 *     autour. C'est ce qui remplace un `direction:ltr` en CSS, et ça marche partout où la chaîne est
 *     peinte — une tuile d'offre, un récapitulatif, le milieu d'une phrase — sans règle à ajouter.
 */
function arabicLeftToRight(text) {
    return ltrIsolate(text.replace(BIDI_MARKS, ''));
}
/** Les marques de direction qu'`Intl` glisse autour d'un montant arabe : RLM, LRM, ALM. */
const BIDI_MARKS = /[\u200E\u200F\u061C]/g;
/** The parts of an `Intl` currency format that spell the NUMBER, as opposed to the symbol around it. */
const NUMERIC_PARTS = new Set([
    'integer',
    'group',
    'decimal',
    'fraction',
    'compact',
]);
/**
 * The amount without any currency symbol: `"1 234,56"`, `"1,234"`, `"1000"`.
 *
 * For surfaces that print the symbol themselves (a seller's price input, a table column headed with the
 * currency) — so the digits still come from the ISO exponent even where the symbol does not come from `Intl`.
 */
export function formatAmount(money, locale = 'fr') {
    const exponent = minorUnitExponent(money.currency);
    const major = toMajor(money.amount, money.currency);
    try {
        // Same rule as `formatMoney`, same reason: an Arabic shop writes its numbers the French way. There is
        // no symbol to preserve here, so the substitution is just a change of locale.
        return new Intl.NumberFormat(isArabicLocale(locale) ? 'fr' : locale, {
            minimumFractionDigits: exponent,
            maximumFractionDigits: exponent,
        }).format(major);
    }
    catch {
        return major.toFixed(exponent);
    }
}
/**
 * A plain, locale-free decimal string: `"1234.56"`, `"1.234"`, `"1000"`.
 *
 * For inputs and round-trips, where a grouping separator or a comma decimal would break `parseFloat`. This
 * is what a price field should be seeded with.
 */
export function toMajorString(amountMinor, currency) {
    return toMajor(amountMinor, currency).toFixed(minorUnitExponent(currency));
}
/**
 * A currency's name in the reader's language: `"dinar algérien"`, `"franc CFA (BCEAO)"`, `"dirham marocain"`.
 *
 * For pickers. 155 hand-written labels × every locale is not a table anyone can keep correct, and `Intl`
 * already ships them — unlike the minor-unit exponent, a translated NAME is exactly the kind of question CLDR
 * is the authority on. Falls back to the bare code, which is still a usable option in a list.
 */
export function currencyLabel(code, locale = 'fr') {
    try {
        return new Intl.DisplayNames([locale], { type: 'currency' }).of(code) ?? code;
    }
    catch {
        return code;
    }
}
/**
 * A fragment that keeps its own direction inside a sentence of any direction.
 *
 * U+2066 LEFT-TO-RIGHT ISOLATE … U+2069 POP DIRECTIONAL ISOLATE: both are zero-width and invisible, they
 * do not change the string's length on screen, and unlike the older LRE/PDF embedding they also stop the
 * amount from disturbing the direction of the words on either side of it.
 *
 * Exported because an amount is spliced into sentences this package never sees — the COD button's
 * « الدفع عند الاستلام · 899,00 د.م. », for one — and those callers must wrap with the SAME two code
 * points, or {@link stripLtrIsolate} would not undo them.
 */
export function ltrIsolate(text) {
    return `\u2066${text}\u2069`;
}
/** The same string without its isolates — for anything that MEASURES the text rather than paints it. */
export function stripLtrIsolate(text) {
    return text.replace(/[\u2066\u2069]/g, '');
}
//# sourceMappingURL=money.js.map